import "./FundRaise.css";
import React, { useState } from "react";
import { Typography } from "antd";
import {
  DollarCircleOutlined,
  GlobalOutlined,
  TeamOutlined,
  FundViewOutlined,
  StockOutlined,
  ProjectOutlined,
  ShopOutlined,
  BarChartOutlined,
  SafetyOutlined,
  RiseOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Footer from "../../components/Footer/Footer";
import Navbar from "../../components/navbar/index.jsx";
import Container from "@mui/material/Container";
import { Grid, Box, TextField, Alert, Snackbar } from "@mui/material";
import fundraiseImage from "../../assets/images/fund-raise.jpg";
import image1 from "../../assets/images/stockmarket2.jpg";

const { Title } = Typography;

// Capital Solutions Data
const capitalSolutions = [
  {
    icon: <DollarCircleOutlined />,
    title: "Debt Funding",
    subtitle: "Growth Without Dilution",
    description: "For businesses with strong cash flows and operational stability, debt can accelerate growth without compromising equity.",
    supports: [
      "Structured Business Loans",
      "Institutional & NBFC Debt",
      "Working Capital & Expansion Funding",
      "Venture Debt",
      "Structured Debt Instruments"
    ],
  },
  {
    icon: <StockOutlined />,
    title: "Pre-IPO Funding",
    subtitle: "Preparing for the Public Market Journey",
    description: "Pre-IPO capital strengthens valuation, governance, and investor confidence before stepping into the public markets.",
    supports: [
      "Benchmark valuation against market comparables",
      "Refine financial governance",
      "Structure equity prior to listing",
      "Onboard strategic Pre-IPO investors"
    ]
  },
  {
    icon: <ProjectOutlined />,
    title: "Project Funding",
    subtitle: "Converting Vision into Fundable Opportunity",
    description: "Large-scale projects require structured feasibility, financial modeling, and capital mix optimization.",
    supports: [
      "DPR evaluation & restructuring",
      "Debt-Equity optimization",
      "Institutional & private capital alignment",
      "Risk mitigation frameworks"
    ]
  },
  {
    icon: <ShopOutlined />,
    title: "Private Equity Funding",
    subtitle: "Strategic Capital for Scale",
    description: "Private Equity is long-term partnership capital for growth-stage companies.",
    supports: [
      "Expansion capital structuring",
      "Minority & strategic equity deals",
      "Investor alignment & cap-table planning",
      "Valuation positioning",
      "Negotiation & term-sheet strategy"
    ]
  }
];

// AFO Journey Steps - FLOWCHART STYLE
const afoSteps = [
  {
    step: 1,
    title: "Business Deep Dive",
    description: "We begin by assessing your business the way institutional investors do.",
    points: [
      "Revenue & profitability diagnostics",
      "Risk exposure mapping",
      "Capital efficiency analysis",
      "Scalability assessment",
      "Realistic valuation benchmarking"
    ],
    icon: "🔍",
    color: "#0f766e"
  },
  {
    step: 2,
    title: "Valuation & Structuring",
    description: "Once clarity is established, we structure the opportunity.",
    points: [
      "Financial model refinement",
      "Dilution planning",
      "Capital requirement optimization",
      "Investment deck restructuring",
      "Equity vs Debt strategy alignment"
    ],
    icon: "📊",
    color: "#0b837a"
  },
  {
    step: 3,
    title: "Investor Mapping",
    description: "Capital raising is about strategic alignment, not mass pitching.",
    points: [
      "Debt providers",
      "Private equity investors",
      "Strategic partners",
      "Pre-IPO investors",
      "Institutional capital pools"
    ],
    icon: "🎯",
    color: "#0f766e"
  },
  {
    step: 4,
    title: "Deal Negotiation",
    description: "Term sheets define long-term impact.",
    points: [
      "Valuation negotiations",
      "Clause evaluation",
      "Deal structuring",
      "Documentation coordination",
      "Final capital closure strategy"
    ],
    icon: "🤝",
    color: "#0b837a"
  },
  {
    step: 5,
    title: "Capital Closure",
    description: "Structured execution leading to successful capital closure.",
    points: [
      "Final term sheet execution",
      "Legal documentation",
      "Fund transfer coordination",
      "Post-closure compliance"
    ],
    icon: "💰",
    color: "#0f766e"
  },
  {
    step: 6,
    title: "Post-Funding Governance",
    description: "Funding is not the finish line — it is the beginning of responsibility.",
    points: [
      "Capital deployment planning",
      "Governance frameworks",
      "Investor reporting structure",
      "Performance milestone tracking",
      "Growth benchmarking"
    ],
    icon: "📈",
    color: "#0b837a"
  }
];

// Why AQT Features
const whyAQT = [
  {
    icon: <SafetyOutlined />,
    title: "Financial Discipline",
    description: "Rigorous financial analysis and disciplined approach to capital structuring"
  },
  {
    icon: <BarChartOutlined />,
    title: "Capital Structuring Expertise",
    description: "Deep expertise in structuring deals that align with your business stage"
  },
  {
    icon: <TeamOutlined />,
    title: "Investor Network Alignment",
    description: "Strategic alignment with the right investors for your specific needs"
  },
  {
    icon: <RiseOutlined />,
    title: "Governance-First Methodology",
    description: "Building sustainable growth through strong governance frameworks"
  },
  {
    icon: <SwapOutlined />,
    title: "Execution-Driven Engagement",
    description: "We don't just advise — we execute alongside your team"
  }
];

export default function FundRaise() {
  const [width] = useState(window.innerWidth);
  const [activeStep, setActiveStep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const navigate = useNavigate();

  // Your Google Apps Script Web App URL
  const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycby6hH8BJvqUVhUkmWuW6iMruI7Yy8QdFJiGablKw0RUVxVGQbgshKXYahj0OzfeZXctvQ/exec';

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    // Create an object from form data
    const formData = new FormData(event.target);
    const formObject = {};
    
    // Convert FormData to object
    formData.forEach((value, key) => {
      formObject[key] = value;
    });
    
    // Add metadata
    formObject.timestamp = new Date().toISOString();
    formObject.source = 'FundRaise Page';

    try {
      // Send to Google Sheets
      const response = await fetch(GOOGLE_SHEETS_URL, {
        method: "POST",
        mode: "no-cors", // This is important for Google Apps Script
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formObject),
      });

      // With no-cors mode, we can't read the response
      // So we'll assume it worked and show success
      setSnackbar({
        open: true,
        message: 'Form submitted successfully! We\'ll contact you soon.',
        severity: 'success'
      });

      // Reset the form
      event.target.reset();

      // Redirect to thank you page after 2 seconds
      setTimeout(() => {
        navigate("/thankyou");
      }, 2000);

    } catch (error) {
      console.error("Error submitting form:", error);
      
      // Show error message
      setSnackbar({
        open: true,
        message: 'Something went wrong. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    const formSection = document.querySelector('.contact-main-container');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Navbar />

      {/* Hero Section with Complementary Financial Theme Image - Optimized for Text Visibility */}
      <div 
        className="fundraisetop" 
        style={{
          position: 'relative',
          backgroundImage: `url(${image1})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: '#fff',
          padding: '120px 0',
          marginTop: '0',
          minHeight: '550px',
          display: 'flex',
          alignItems: 'center',
          position: 'relative'
        }}
      >
        {/* Enhanced gradient overlay that complements the teal theme and ensures text visibility */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(95deg, rgba(10, 60, 55, 0.98) 0%, rgba(15, 118, 110, 0.92) 40%, rgba(15, 118, 110, 0.75) 70%, rgba(157, 218, 213, 0.45) 100%)',
          zIndex: 1
        }} />

        {/* Decorative elements */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 70% 30%, rgba(157, 218, 213, 0.15) 0%, transparent 50%)',
          zIndex: 1
        }} />

        <div className="row" style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: '1400px', margin: '0 auto', padding: '0 40px' }}>
          <motion.div 
            className="col-heading"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ marginBottom: '30px' }}
          >
            <h1 style={{ 
              fontSize: 'clamp(2.8rem, 6vw, 4.2rem)',
              fontWeight: '800',
              marginBottom: '1.2rem',
              textShadow: '3px 3px 6px rgba(0, 20, 15, 0.5)',
              color: '#ffffff',
              letterSpacing: '-0.02em',
              lineHeight: '1.2'
            }}>
              FUNDRAISE WITH AQT DIRECT
            </h1>
            <p className="hero-subtitle" style={{
              fontSize: 'clamp(1.3rem, 2.2vw, 1.8rem)',
              color: '#9ddad5',
              fontWeight: '500',
              marginBottom: '2rem',
              textShadow: '2px 2px 4px rgba(0, 20, 15, 0.4)',
              letterSpacing: '0.02em'
            }}>
              Capital. Structured. Positioned. Executed.
            </p>
          </motion.div>
          
          <motion.div 
            className="col-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <p className="hero-description" style={{
              fontSize: 'clamp(1.1rem, 1.6vw, 1.3rem)',
              lineHeight: '1.8',
              marginBottom: '2rem',
              color: '#ffffff',
              textShadow: '1px 1px 3px rgba(0, 0, 0, 0.3)',
              maxWidth: '800px',
              fontWeight: '400',
              opacity: 0.95
            }}>
              At AQT Direct, fundraising is not about sending decks to investors.<br className="desktop-break" />
              It is about understanding which capital fits your stage, structuring your 
              business to deserve it, and executing the deal with financial discipline.
            </p>
            <p className="hero-emphasis" style={{
              fontSize: 'clamp(1.2rem, 1.9vw, 1.5rem)',
              fontWeight: '600',
              color: '#ffffff',
              borderLeft: '5px solid #9ddad5',
              paddingLeft: '25px',
              marginTop: '35px',
              textShadow: '2px 2px 4px rgba(0, 20, 15, 0.4)',
              maxWidth: '800px',
              fontStyle: 'italic',
              lineHeight: '1.6'
            }}>
              Different businesses require different capital strategies.<br />
              We don't "arrange funds." We architect funding journeys.
            </p>
          </motion.div>
        </div>

        {/* Bottom fade effect for smooth transition */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '80px',
          background: 'linear-gradient(to top, rgba(255,255,255,1), transparent)',
          zIndex: 2
        }} />
      </div>

      {/* Capital Solutions Section */}
      <div className="capital-solutions-container">
        <div className="section-header">
          <h2 className="header">Our Capital Solutions</h2>
          <p className="section-subheading">
            Tailored funding strategies for every stage of your business journey
          </p>
        </div>

        <div className="solutions-grid">
          {capitalSolutions.map((solution, index) => (
            <motion.div
              key={index}
              className="solution-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="solution-icon">{solution.icon}</div>
              <h3 className="solution-title">{solution.title}</h3>
              <p className="solution-subtitle">{solution.subtitle}</p>
              <p className="solution-description">{solution.description}</p>
              
              <div className="solution-supports">
                <h4>We support:</h4>
                <ul>
                  {solution.supports.map((item, idx) => (
                    <li key={idx}>
                      <CheckCircleOutlined className="check-icon" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* AFO Section - FLOWCHART DESIGN */}
      <div className="afo-section">
        <div className="afo-header">
          <h2 className="afo-title">Introducing</h2>
          <h3 className="afo-subtitle">
            Hire Your <span style={{ color: '#0F766E' }}>AQT Financial Officer (AFO)</span>
          </h3>
          <p className="afo-description">
            Your Dedicated Capital Strategist — From Start to Finish
          </p>
          <div className="afo-highlight">
            <p>
              Many businesses fail to raise funds not due to lack of investors —<br />
              but due to lack of structured financial leadership.
            </p>
            <p className="afo-emphasis">
              With the AQT Financial Officer Model, your company gets a dedicated capital 
              expert who works alongside your leadership team until funding is secured 
              and deployed strategically.
            </p>
            <p className="afo-tagline">This is beyond advisory. This is execution partnership.</p>
            
            {/* Apply Now Button */}
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <Button
                onClick={scrollToForm}
                variant="contained"
                style={{
                  background: 'linear-gradient(90deg, #0f766e, #9ddad5)',
                  color: '#fff',
                  padding: '12px 40px',
                  fontWeight: 'bold',
                  borderRadius: '25px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                  transition: 'transform 0.3s ease',
                  fontSize: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                Apply Now
              </Button>
            </div>
          </div>
        </div>

        {/* FLOWCHART JOURNEY */}
        <div className="afo-journey">
          <h3 className="journey-title">The AQT Financial Officer Journey</h3>
          
          {/* Flowchart Container */}
          <div className="flowchart-container">
            
            {/* Desktop Flowchart (Row 1: Steps 1-3) */}
            <div className="flowchart-row desktop-only">
              {afoSteps.slice(0, 3).map((step, index) => (
                <React.Fragment key={step.step}>
                  <motion.div 
                    className="flowchart-node"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.15 }}
                    onMouseEnter={() => setActiveStep(step.step)}
                    onMouseLeave={() => setActiveStep(null)}
                  >
                    <div className="node-content">
                      <div className="node-icon" style={{ background: `linear-gradient(135deg, ${step.color}, #9ddad5)` }}>
                        <span>{step.icon}</span>
                      </div>
                      <div className="node-number">Step {step.step}</div>
                      <h4 className="node-title">{step.title}</h4>
                      
                      {/* Popup Details on Hover */}
                      {activeStep === step.step && (
                        <motion.div 
                          className="node-popup"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <p className="popup-description">{step.description}</p>
                          <ul className="popup-points">
                            {step.points.map((point, idx) => (
                              <li key={idx}>
                                <CheckCircleOutlined className="popup-check" />
                                {point}
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                  
                  {/* Arrow between nodes (except after last) */}
                  {index < 2 && (
                    <div className="flowchart-arrow right">
                      <ArrowRightOutlined />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Vertical Connector Line for Desktop */}
            <div className="flowchart-vertical-line desktop-only"></div>

            {/* Desktop Flowchart (Row 2: Steps 4-6) */}
            <div className="flowchart-row desktop-only">
              {afoSteps.slice(3, 6).map((step, index) => (
                <React.Fragment key={step.step}>
                  <motion.div 
                    className="flowchart-node"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: (index + 3) * 0.15 }}
                    onMouseEnter={() => setActiveStep(step.step)}
                    onMouseLeave={() => setActiveStep(null)}
                  >
                    <div className="node-content">
                      <div className="node-icon" style={{ background: `linear-gradient(135deg, ${step.color}, #9ddad5)` }}>
                        <span>{step.icon}</span>
                      </div>
                      <div className="node-number">Step {step.step}</div>
                      <h4 className="node-title">{step.title}</h4>
                      
                      {/* Popup Details on Hover */}
                      {activeStep === step.step && (
                        <motion.div 
                          className="node-popup"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <p className="popup-description">{step.description}</p>
                          <ul className="popup-points">
                            {step.points.map((point, idx) => (
                              <li key={idx}>
                                <CheckCircleOutlined className="popup-check" />
                                {point}
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                  
                  {/* Arrow between nodes (except after last) */}
                  {index < 2 && (
                    <div className="flowchart-arrow right">
                      <ArrowRightOutlined />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Mobile Flowchart (Vertical Stack) */}
            <div className="flowchart-vertical mobile-only">
              {afoSteps.map((step, index) => (
                <React.Fragment key={step.step}>
                  <motion.div 
                    className="flowchart-node mobile"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    onClick={() => setActiveStep(activeStep === step.step ? null : step.step)}
                  >
                    <div className="node-content">
                      <div className="node-icon" style={{ background: `linear-gradient(135deg, ${step.color}, #9ddad5)` }}>
                        <span>{step.icon}</span>
                      </div>
                      <div className="node-info">
                        <div className="node-number">Step {step.step}</div>
                        <h4 className="node-title">{step.title}</h4>
                      </div>
                      <DownOutlined className="expand-icon" />
                    </div>
                    
                    {/* Expandable Details on Mobile */}
                    {activeStep === step.step && (
                      <motion.div 
                        className="node-details"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ duration: 0.3 }}
                      >
                        <p className="details-description">{step.description}</p>
                        <ul className="details-points">
                          {step.points.map((point, idx) => (
                            <li key={idx}>
                              <CheckCircleOutlined className="details-check" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </motion.div>
                  
                  {/* Down Arrow for Mobile (except after last) */}
                  {index < afoSteps.length - 1 && (
                    <div className="flowchart-arrow down mobile-only">
                      <DownOutlined />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Who Should Engage Section */}
      <div className="target-audience">
        <div className="section-header">
          <h2 className="header">Who Should Engage with AQT?</h2>
        </div>
        <div className="audience-grid">
          <div className="audience-item">
            <CheckCircleOutlined className="audience-icon" />
            <p>Growth-stage businesses seeking expansion</p>
          </div>
          <div className="audience-item">
            <CheckCircleOutlined className="audience-icon" />
            <p>Companies preparing for IPO</p>
          </div>
          <div className="audience-item">
            <CheckCircleOutlined className="audience-icon" />
            <p>Capital-intensive project owners</p>
          </div>
          <div className="audience-item">
            <CheckCircleOutlined className="audience-icon" />
            <p>Founders needing structured financial leadership</p>
          </div>
          <div className="audience-item">
            <CheckCircleOutlined className="audience-icon" />
            <p>Businesses looking for strategic private equity</p>
          </div>
        </div>
        <p className="audience-message">
          If you are serious about scaling — your capital strategy must be serious too.
        </p>
      </div>

      {/* Why AQT Section */}
      <div className="why-aqt-section">
        <div className="section-header">
          <h2 className="header">Why AQT Direct?</h2>
        </div>
        <div className="why-grid">
          {whyAQT.map((item, index) => (
            <motion.div
              key={index}
              className="why-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="why-icon">{item.icon}</div>
              <h4>{item.title}</h4>
              <p>{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="why-conclusion">
          We do not operate as intermediaries. We operate as capital strategists.
        </p>
      </div>

      {/* CTA Section */}
      <motion.section
        className="ready-to-raise"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
          backgroundColor: "white",
          padding: "40px 20px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          gap: "40px",
        }}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <img
          src={image1}
          alt="Fundraising"
          style={{
            width: "100%",
            maxWidth: "300px",
            height: "auto",
            borderRadius: "12px",
          }}
        />

        <div style={{ textAlign: "left", maxWidth: "600px" }}>
          <h2 className="raise-header">
            Ready to Structure Your Capital Journey?
          </h2>
          <p className="raise-description">
            Whether you require Debt Funding, Pre-IPO capital, Project Financing, 
            Private Equity, or a Dedicated AQT Financial Officer — we help you move 
            from intention to execution.
          </p>
          <p className="raise-emphasis">
            Capital is available.<br />
            The real question is — are you structured for it?
          </p>
        </div>
      </motion.section>

      {/* Contact Form Section - SIMPLIFIED */}
      <Container
        className="contact-main-container"
        style={{
          backgroundImage: `url(${fundraiseImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          padding: "50px 20px",
          position: "relative",
          color: "#fff",
          width: "100vw",
          maxWidth: "100%",
          backgroundColor: "#097b72",
          marginTop: "0px",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 1,
          }}
        />

        <Grid container spacing={4} style={{ position: "relative", zIndex: 2 }}>
          <Grid item xs={12} md={6}>
            <Box
              className="left-section"
              style={{
                marginLeft: width > 1080 ? "150px" : "59px",
              }}
            >
              <div className="typography-heading">
                Start Your Capital Journey Today
              </div>
              <p className="fund-description">
                Take the first step towards structured fundraising. Our team of 
                capital strategists will guide you through every phase of the journey.
              </p>
              
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              className="form-container"
              style={{
                padding: "20px",
                borderRadius: "15px",
                boxShadow: "0 8px 15px rgba(0, 0, 0, 0.15)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "scale(1.02)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "scale(1)")
              }
            >
              <form onSubmit={handleFormSubmit}>
                <Grid container spacing={3}>
                  {/* Full Name - Required */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name *"
                      name="fullName"
                      required
                      variant="outlined"
                      size="small"
                      disabled={loading}
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "5px",
                        "& fieldset": { border: "none" },
                        "& .MuiOutlinedInput-root": {
                          borderBottom: "2px solid #ccc",
                          "&:hover": { borderBottom: "2px solid #0b837a" },
                          "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                        },
                      }}
                    />
                  </Grid>

                  {/* Phone Number - Required */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Mobile Number *"
                      type="tel"
                      name="phoneNo"
                      required
                      variant="outlined"
                      size="small"
                      disabled={loading}
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "5px",
                        "& fieldset": { border: "none" },
                        "& .MuiOutlinedInput-root": {
                          borderBottom: "2px solid #ccc",
                          "&:hover": { borderBottom: "2px solid #0b837a" },
                          "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                        },
                      }}
                    />
                  </Grid>

                  {/* Email Address - Required */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address *"
                      type="email"
                      name="email"
                      required
                      variant="outlined"
                      size="small"
                      disabled={loading}
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "5px",
                        "& fieldset": { border: "none" },
                        "& .MuiOutlinedInput-root": {
                          borderBottom: "2px solid #ccc",
                          "&:hover": { borderBottom: "2px solid #0b837a" },
                          "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                        },
                      }}
                    />
                  </Grid>

                  {/* Company Name - Optional */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company Name (Optional)"
                      type="text"
                      name="companyName"
                      variant="outlined"
                      size="small"
                      disabled={loading}
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "5px",
                        "& fieldset": { border: "none" },
                        "& .MuiOutlinedInput-root": {
                          borderBottom: "2px solid #ccc",
                          "&:hover": { borderBottom: "2px solid #0b837a" },
                          "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                        },
                      }}
                    />
                  </Grid>

                  {/* Interested In - Optional */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Interested In (Debt/Pre-IPO/Project/PE/AFO) - Optional"
                      name="interest"
                      variant="outlined"
                      size="small"
                      disabled={loading}
                      sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: "5px",
                        "& fieldset": { border: "none" },
                        "& .MuiOutlinedInput-root": {
                          borderBottom: "2px solid #ccc",
                          "&:hover": { borderBottom: "2px solid #0b837a" },
                          "&.Mui-focused": { borderBottom: "2px solid #0b837a" },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      style={{
                        background: loading ? '#ccc' : 'linear-gradient(90deg, #0b837a, #9ddad5)',
                        color: "#fff",
                        padding: "12px 20px",
                        fontWeight: "bold",
                        borderRadius: "25px",
                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                        transition: "transform 0.3s ease",
                        fontSize: "16px",
                        cursor: loading ? 'not-allowed' : 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) {
                          e.currentTarget.style.transform = "scale(1.05)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                      fullWidth
                    >
                      {loading ? 'SUBMITTING...' : 'START YOUR CAPITAL JOURNEY'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </>
  );
}